import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, PhoneCall, UserPlus, UserRound, X } from 'lucide-react';
import { Alert, Badge, Button, Combobox, DatePicker, Drawer, Input, Skeleton, Spinner, type SelectOption } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { PERMISSIONS } from '@nova/shared';
import { useLang } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { formatDateTime, toDateInput } from '@/lib/utils';
import { useReferenceItems } from '@/features/settings/hooks';
import type { ReferenceItem } from '@/features/settings/types';
import { onlineBookingApi, onlineBookingKeys } from './api';
import type { OnlineBookingDetail } from './api';

interface Props {
  requestId: string | null;
  canReview: boolean;
  initialErrorCode?: string;
  onClose: () => void;
}

interface AppointmentStatusTranslator { (key: string, options?: Record<string, unknown>): string }

export function OnlineBookingReviewDrawer({ requestId, canReview: canReviewProp, initialErrorCode, onClose }: Props) {
  const lang = useLang();
  const { t } = useTranslation('onlineBooking');
  const { t: tAppointments } = useTranslation('appointments');
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canAny = useAuthStore((s) => s.canAny);
  const canView = canAny(PERMISSIONS.ONLINE_BOOKING_VIEW);
  const canReview = canReviewProp && canView;
  const [reasonId, setReasonId] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [contactNote, setContactNote] = useState('');
  const [providerId, setProviderId] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [startAt, setStartAt] = useState('');
  const [outcome, setOutcome] = useState('NO_ANSWER');
  const [error, setError] = useState('');
  const detail = useQuery({
    queryKey: onlineBookingKeys.detail(requestId ?? ''),
    queryFn: () => onlineBookingApi.detail(requestId as string),
    enabled: Boolean(requestId && canView),
  });
  const reasons = useReferenceItems(requestId && canReview ? 'ONLINE_BOOKING_REJECTION_REASON' : null);
  const row = detail.data;
  const availability = useQuery({
    queryKey: [...onlineBookingKeys.detail(requestId ?? ''), 'availability', requestedDate],
    queryFn: () => onlineBookingApi.availability(requestId as string, requestedDate),
    enabled: Boolean(requestId && canReview && requestedDate),
    staleTime: 10_000,
  });
  const activeReasons = useMemo(
    () => (reasons.data?.data ?? []).filter((reason) => reason.isActive),
    [reasons.data],
  );
  const selectedReason = activeReasons.find((reason) => reason.id === reasonId);
  const otherReason = selectedReason?.code?.toUpperCase() === 'OTHER';
  const hasPatient = Boolean(row?.appointment.patientId);
  const providers = availability.data?.providers ?? [];
  const providerSlots = availability.data?.slots ?? [];
  const availableSlots = availability.data?.slots.filter((slot) => slot.providerId === providerId) ?? [];
  const selectedSlotAvailable = availableSlots.some((slot) => slot.startAt === startAt);

  useEffect(() => {
    if (!requestId || !canReview || row?.appointment.status !== 'PENDING') return;
    void onlineBookingApi.claim(requestId)
      .then(() => { void qc.invalidateQueries({ queryKey: onlineBookingKeys.detail(requestId) }); })
      .catch((e: unknown) => setError(localizeConflict(e, t)));
    const interval = window.setInterval(() => { void onlineBookingApi.claim(requestId).catch(() => undefined); }, 30_000);
    return () => { window.clearInterval(interval); void onlineBookingApi.release(requestId).catch(() => undefined); };
  }, [requestId, canReview, row?.appointment.status, qc, t]);

  useEffect(() => {
    if (!row) return;
    setProviderId(row.appointment.providerId);
    setRequestedDate(toDateInput(row.requestedSlot.startAt));
    setStartAt(row.requestedSlot.startAt);
    setReasonId('');
    setRejectNote('');
    setContactNote('');
  }, [row?.appointmentId, row?.version]);

  useEffect(() => {
    if (!initialErrorCode) return;
    setError(t(`drawer.conflicts.${initialErrorCode}`, { defaultValue: t('drawer.conflicts.default') }));
  }, [initialErrorCode, t]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: onlineBookingKeys.all });
    void qc.invalidateQueries({ queryKey: ['notifications', 'counts'] });
  };
  const mutate = async (run: () => Promise<unknown>) => {
    setError('');
    try {
      await run();
      invalidate();
      if (requestId) await qc.invalidateQueries({ queryKey: onlineBookingKeys.detail(requestId) });
    } catch (e) {
      setError(localizeConflict(e, t));
      if (requestId) await qc.invalidateQueries({ queryKey: onlineBookingKeys.detail(requestId) });
    }
  };
  const serviceNames = Array.isArray(row?.service)
    ? row.service.filter((service): service is NonNullable<typeof service> => Boolean(service)).map((service) => lang === 'AR' ? service.nameAr : service.nameEn || service.nameAr).filter(Boolean).join(t('listSeparator'))
    : lang === 'AR' ? row?.service?.nameAr : row?.service?.nameEn || row?.service?.nameAr;
  const procedureNames = row?.appointment.procedures?.map((procedure) =>
    lang === 'AR'
      ? procedure?.procedure?.nameAr || procedure?.category?.nameAr
      : procedure?.procedure?.nameEn || procedure?.category?.nameEn || procedure?.procedure?.nameAr || procedure?.category?.nameAr,
  ).filter(Boolean).join(t('listSeparator'));

  if (!canView) return null;
  return <Drawer
    open={Boolean(requestId && canView)}
    onOpenChange={(open) => { if (!open) onClose(); }}
    title={t('drawer.title')}
    subtitle={row?.submittedName}
    width="lg"
    data-testid="online-booking-review-drawer"
    footer={row && canReview ? <ReviewActions
      row={row}
      hasPatient={hasPatient}
      canReject={row.appointment.status === 'PENDING' && Boolean(reasonId) && (!otherReason || Boolean(rejectNote.trim()))}
      canChangeSlot={row.appointment.status === 'PENDING' && Boolean(providerId && startAt && selectedSlotAvailable)}
      onConfirm={() => void mutate(() => onlineBookingApi.confirm(row.appointmentId, { version: row.version }))}
      onConfirmWithChanges={() => void mutate(() => onlineBookingApi.confirm(row.appointmentId, { version: row.version, providerId, startAt: startAt.slice(0, 16) }))}
      onReject={() => void mutate(() => onlineBookingApi.reject(row.appointmentId, { version: row.version, reasonId, note: rejectNote || undefined }))}
      t={t}
    /> : null}
  >
    {detail.isLoading || !row ? <div className="flex items-center gap-2 p-4"><Spinner />{t('drawer.loading')}</div> : <div className="space-y-4 pb-1">
      {error ? <Alert tone="danger" title={t('drawer.actionError')}>{error}</Alert> : null}
      {row.claimHolder ? <Alert tone={row.claimHolder.id === user?.id ? 'info' : 'warning'} title={row.claimHolder.id === user?.id ? t('drawer.claimSelf') : `${t('drawer.beingHandledBy')} ${localizedName(row.claimHolder, lang)}`} /> : null}
      <RequestDetails row={row} lang={lang} serviceNames={serviceNames || procedureNames || '—'} t={t} />
      <PatientResolution row={row} hasPatient={hasPatient} canReview={canReview} lang={lang} t={t} mutate={mutate} />
      {canReview ? <ChangeSlot
        providerId={providerId}
        requestedDate={requestedDate}
        startAt={startAt}
        requestedStartAt={row.requestedSlot.startAt}
        providers={providers}
        providerSlots={providerSlots}
        slots={availableSlots}
        loading={availability.isLoading}
        lang={lang}
        t={t}
        onProviderChange={(next) => { setProviderId(next); setStartAt(''); }}
        onDateChange={(next) => { setRequestedDate(next); setStartAt(''); }}
        onSlotChange={setStartAt}
      /> : null}
      {canReview ? <ContactAttempt disabled={row.appointment.status !== 'PENDING'} outcome={outcome} note={contactNote} t={t} onOutcome={setOutcome} onNote={setContactNote} onSubmit={() => void mutate(() => onlineBookingApi.contactAttempt(row.appointmentId, { version: row.version, outcome, note: contactNote || undefined }))} /> : null}
      {canReview ? <RejectSection reasons={activeReasons} reasonId={reasonId} note={rejectNote} otherReason={Boolean(otherReason)} lang={lang} t={t} onReason={setReasonId} onNote={setRejectNote} /> : null}
      <LeadHistory row={row} lang={lang} t={t} tAppointments={tAppointments} />
      <EarlierRequests row={row} lang={lang} t={t} tAppointments={tAppointments} />
      <ContactHistory row={row} lang={lang} t={t} />
    </div>}
  </Drawer>;
}

function ReviewActions({ row, hasPatient, canReject, canChangeSlot, onConfirm, onConfirmWithChanges, onReject, t }: {
  row: OnlineBookingDetail; hasPatient: boolean; canReject: boolean; canChangeSlot: boolean;
  onConfirm: () => void; onConfirmWithChanges: () => void; onReject: () => void; t: (key: string) => string;
}) {
  return <div className="flex w-full flex-wrap gap-2 max-lg:flex-col">
    <Button data-testid="online-booking-confirm" icon={Check} disabled={!hasPatient || row.appointment.status !== 'PENDING'} onClick={onConfirm}>{t('drawer.confirm')}</Button>
    <Button data-testid="online-booking-confirm-with-changes" variant="secondary" disabled={!hasPatient || !canChangeSlot} onClick={onConfirmWithChanges}>{t('drawer.confirmWithChanges')}</Button>
    <Button data-testid="online-booking-reject" variant="danger" disabled={!canReject} onClick={onReject}><X className="h-4 w-4" />{t('drawer.reject')}</Button>
  </div>;
}

function RequestDetails({ row, lang, serviceNames, t }: { row: OnlineBookingDetail; lang: 'AR' | 'EN'; serviceNames: string; t: (key: string) => string }) {
  const blocks = row.conflictCheck.hardBlocks ?? [];
  return <section className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm">
    <h3 className="font-bold">{t('drawer.details')}</h3>
    <p>{row.submittedName} · {row.rawPhone}</p>
    {row.appointment.patient ? <p data-testid="online-booking-linked-patient" className="text-xs text-slate-600">{t('drawer.linkedFile')}: {[row.appointment.patient.firstName, row.appointment.patient.fatherName, row.appointment.patient.grandfatherName, row.appointment.patient.familyName].filter(Boolean).join(' ')} · <bdi dir="ltr">{row.appointment.patient.fileNumber}</bdi></p> : null}
    {row.email ? <p>{row.email}</p> : null}
    {row.notes ? <p>{row.notes}</p> : null}
    <p>{t('drawer.service')}: {serviceNames}</p>
    <p>{t('drawer.requestedSlot')}: <bdi>{formatDateTime(row.requestedSlot.startAt, lang)}</bdi></p>
    <Badge tone={blocks.length ? 'red' : 'emerald'}>{blocks.length ? t('drawer.conflict') : t('drawer.available')}</Badge>
    {blocks.length ? <div role="list" className="space-y-1 pt-1"><strong className="block text-xs">{t('drawer.cannotConfirm')}</strong>{blocks.map((block, index) => <p role="listitem" key={`${String(block)}-${index}`} className="text-xs">{blockMessage(block, t)}</p>)}</div> : null}
  </section>;
}

function PatientResolution({ row, hasPatient, canReview, lang, t, mutate }: {
  row: OnlineBookingDetail; hasPatient: boolean; canReview: boolean; lang: 'AR' | 'EN';
  t: (key: string, options?: Record<string, unknown>) => string; mutate: (run: () => Promise<unknown>) => Promise<void>;
}) {
  const fullName = (patient: OnlineBookingDetail['patientMatches'][number]) => [patient.firstName, patient.fatherName, patient.grandfatherName, patient.familyName].filter(Boolean).join(' ');
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.patient')}</h3>
    {hasPatient ? <Badge tone="emerald">{t('drawer.linked')}</Badge> : <>
      <p className="text-xs text-slate-500">{t('drawer.suggestions')}</p>
      {row.patientMatches.map((patient) => <div key={patient.id} className="flex flex-wrap items-center gap-2 rounded border p-2">
        <UserRound className="h-4 w-4" />
        <span className="min-w-0 flex-1 text-xs">{patient.fileNumber} · {fullName(patient)} · {patient.phoneE164}{patient.lastVisitAt ? ` · ${t('drawer.lastVisit')}: ${formatDateTime(patient.lastVisitAt, lang)}` : ''}</span>
        {canReview ? <Button data-testid="online-booking-link-patient" size="sm" variant="secondary" onClick={() => void mutate(() => onlineBookingApi.resolvePatient(row.appointmentId, { version: row.version, kind: 'link', patientId: patient.id }))}>{t('drawer.link')}</Button> : null}
      </div>)}
      {canReview ? <Button data-testid="online-booking-create-patient" size="sm" icon={UserPlus} variant="secondary" onClick={() => void mutate(() => onlineBookingApi.resolvePatient(row.appointmentId, { version: row.version, kind: 'createNew' }))}>{t('drawer.createPatient')}</Button> : null}
    </>}
  </section>;
}

function ChangeSlot({ providerId, requestedDate, startAt, requestedStartAt, providers, providerSlots, slots, loading, lang, t, onProviderChange, onDateChange, onSlotChange }: {
  providerId: string; requestedDate: string; startAt: string; requestedStartAt: string;
  providers: Array<{ id: string; nameAr: string; nameEn?: string | null }>;
  providerSlots: Array<{ providerId: string; startAt: string; endAt: string }>;
  slots: Array<{ providerId: string; startAt: string; endAt: string }>;
  loading: boolean; lang: 'AR' | 'EN'; t: (key: string, options?: Record<string, unknown>) => string;
  onProviderChange: (id: string) => void; onDateChange: (date: string) => void; onSlotChange: (startAt: string) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedSlotRef = useRef<HTMLButtonElement>(null);
  const slotSections = useMemo(() => [
    { key: 'morning', slots: slots.filter((slot) => clinicWallClockHour(slot.startAt) < 12) },
    { key: 'afternoon', slots: slots.filter((slot) => clinicWallClockHour(slot.startAt) >= 12 && clinicWallClockHour(slot.startAt) < 17) },
    { key: 'evening', slots: slots.filter((slot) => clinicWallClockHour(slot.startAt) >= 17) },
  ], [slots]);
  const providerOptions: SelectOption[] = providers.map((provider) => {
    const count = providerSlots.filter((slot) => slot.providerId === provider.id).length;
    return {
      value: provider.id,
      label: `${lang === 'AR' ? provider.nameAr : provider.nameEn || provider.nameAr} · ${count}`,
    };
  });

  // Scroll only the grid: scrollIntoView would also scroll the drawer itself.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const selected = selectedSlotRef.current;
    if (!container || !selected) return;
    const offset = selected.getBoundingClientRect().top - container.getBoundingClientRect().top;
    if (offset < 0 || offset + selected.offsetHeight > container.clientHeight)
      container.scrollTop += offset + selected.offsetHeight / 2 - container.clientHeight / 2;
  }, [startAt, slots]);

  const providerPicker = providers.length > 6 ? <Combobox
    aria-label={t('drawer.provider')}
    data-testid="online-booking-provider-combobox"
    value={providerId}
    options={providerOptions}
    onChange={onProviderChange}
    clearable={false}
    placeholder={t('drawer.provider')}
    className="min-w-0"
  /> : <div role="radiogroup" aria-label={t('drawer.provider')} className="flex flex-wrap gap-1.5">
    {providers.map((provider) => {
      const count = providerSlots.filter((slot) => slot.providerId === provider.id).length;
      const selected = provider.id === providerId;
      return <button
        key={provider.id}
        type="button"
        role="radio"
        aria-checked={selected}
        data-testid="online-booking-provider-option"
        data-value={provider.id}
        onClick={() => onProviderChange(provider.id)}
        className={`inline-flex min-h-8 items-center gap-1.5 rounded border px-2 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'} ${count === 0 && !selected ? 'opacity-50' : ''}`}
      >
        {lang === 'AR' ? provider.nameAr : provider.nameEn || provider.nameAr}<span className={`rounded-full px-1.5 text-[10px] tabular-nums ${selected ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
      </button>;
    })}
  </div>;

  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.confirmSlot')}</h3>
    <DatePicker aria-label={t('drawer.date')} data-testid="online-booking-slot-date" value={requestedDate} onChange={(next) => onDateChange(next ?? '')} />
    <div data-testid="online-booking-provider-select" data-value={providerId}>{providerPicker}</div>
    <div
      ref={scrollContainerRef}
      role="radiogroup"
      aria-label={t('drawer.requestedSlot')}
      aria-disabled={loading || slots.length === 0}
      aria-busy={loading || undefined}
      data-testid="online-booking-slot-select"
      data-value={startAt}
      className="max-h-60 space-y-2 overflow-y-auto rounded border border-slate-200 p-2"
    >
      {loading ? <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">{Array.from({ length: 12 }, (_, index) => <Skeleton key={index} h={32} className="rounded" />)}</div> : slots.length === 0 ? <p className="py-2 text-xs text-slate-500">{t('drawer.noSlots')}</p> : slotSections.map(({ key, slots: sectionSlots }) => sectionSlots.length ? <section key={key} className="space-y-1.5">
        <h4 className="text-[11px] font-bold text-slate-500">{t(`drawer.${key}`)} ({sectionSlots.length})</h4>
        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
          {sectionSlots.map((slot) => {
            const selected = slot.startAt === startAt;
            const requested = slot.startAt === requestedStartAt;
            return <button
              key={slot.startAt}
              ref={selected ? selectedSlotRef : undefined}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid="online-booking-slot-option"
              data-value={slot.startAt}
              title={requested ? t('drawer.requestedTime') : undefined}
              onClick={() => onSlotChange(slot.startAt)}
              className={`relative inline-flex h-8 items-center justify-center gap-1 rounded border px-1 text-xs font-bold tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              {requested ? <><span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" /><span className="sr-only">{t('drawer.requestedTime')}</span></> : null}
              <span dir="ltr">{slot.startAt.slice(11, 16)}</span>
            </button>;
          })}
        </div>
      </section> : null)}
    </div>
    <p className="text-[11px] text-slate-500">{t('drawer.availabilityHint')}</p>
  </section>;
}

function clinicWallClockHour(startAt: string): number {
  const hour = Number(startAt.slice(11, 13));
  return Number.isFinite(hour) ? hour : 0;
}

function ContactAttempt({ disabled, outcome, note, t, onOutcome, onNote, onSubmit }: {
  disabled: boolean; outcome: string; note: string; t: (key: string) => string;
  onOutcome: (value: string) => void; onNote: (value: string) => void; onSubmit: () => void;
}) {
  const outcomes = ['NO_ANSWER', 'REACHED', 'LEFT_MESSAGE', 'WRONG_NUMBER'];
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.contactAttempt')}</h3>
    <div className="flex flex-wrap gap-2">
      <select data-testid="online-booking-call-outcome" data-value={outcome} value={outcome} disabled={disabled} onChange={(event) => onOutcome(event.target.value)} className="min-h-10 min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 text-xs">
        {outcomes.map((value) => <option key={value} value={value} data-value={value}>{t(`drawer.outcomes.${value}`)}</option>)}
      </select>
      <Button data-testid="online-booking-log-call-attempt" icon={PhoneCall} variant="secondary" disabled={disabled} onClick={onSubmit}>{t('drawer.logCall')}</Button>
    </div>
    <Input data-testid="online-booking-contact-note" value={note} onChange={(event) => onNote(event.target.value)} placeholder={t('drawer.optionalNote')} />
  </section>;
}

function RejectSection({ reasons, reasonId, note, otherReason, lang, t, onReason, onNote }: {
  reasons: ReferenceItem[]; reasonId: string; note: string; otherReason: boolean; lang: 'AR' | 'EN';
  t: (key: string) => string; onReason: (id: string) => void; onNote: (note: string) => void;
}) {
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.reason')}</h3>
    <select data-testid="online-booking-reject-reason" data-value={reasons.find((reason) => reason.id === reasonId)?.code ?? ''} value={reasonId} onChange={(event) => onReason(event.target.value)} className="min-h-10 w-full rounded border border-slate-300 bg-white px-2 text-sm">
      <option value="" data-value="">{t('drawer.selectReason')}</option>
      {reasons.map((reason) => <option key={reason.id} value={reason.id} data-value={reason.code ?? reason.id}>{lang === 'AR' ? reason.nameAr : reason.nameEn || reason.nameAr}</option>)}
    </select>
    <Input data-testid="online-booking-reject-note" value={note} onChange={(event) => onNote(event.target.value)} placeholder={otherReason ? t('drawer.otherReasonNote') : t('drawer.optionalNote')} />
  </section>;
}

function LeadHistory({ row, lang, t, tAppointments }: { row: OnlineBookingDetail; lang: 'AR' | 'EN'; t: (key: string, options?: Record<string, unknown>) => string; tAppointments: AppointmentStatusTranslator }) {
  if (!row.lead) return null;
  const requests = row.lead.bookingRequests ?? [];
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.leadHistory')}</h3>
    <p className="text-xs text-slate-600">{t('drawer.status')}: {t(`drawer.leadStatus.${row.lead.status}`, { defaultValue: row.lead.status })} · {t('drawer.bookingRequests')}</p>
    {requests.length ? requests.map((request) => <div key={request.appointmentId} className="flex flex-wrap gap-x-2 text-xs text-slate-600">
      <bdi>{formatDateTime(request.createdAt, lang)}</bdi>
      <span>{tAppointments(`status.${request.appointment?.status ?? 'PENDING'}`, { defaultValue: request.appointment?.status ?? 'PENDING' })}</span>
      <span>{t(`drawer.resolution.${request.patientResolution ?? 'UNRESOLVED'}`, { defaultValue: request.patientResolution ?? 'UNRESOLVED' })}</span>
    </div>) : <p className="text-xs text-slate-500">{t('drawer.noLeadRequests')}</p>}
  </section>;
}

function EarlierRequests({ row, lang, t, tAppointments }: { row: OnlineBookingDetail; lang: 'AR' | 'EN'; t: (key: string) => string; tAppointments: AppointmentStatusTranslator }) {
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.earlierRequests')}</h3>
    {row.earlierRequests.length ? row.earlierRequests.map((request) => <p key={request.appointmentId} className="text-xs text-slate-600">
      <bdi>{formatDateTime(request.createdAt, lang)}</bdi> · {tAppointments(`status.${request.appointment.status}`, { defaultValue: request.appointment.status })}
    </p>) : <p className="text-xs text-slate-500">{t('drawer.noEarlierRequests')}</p>}
  </section>;
}

function ContactHistory({ row, lang, t }: { row: OnlineBookingDetail; lang: 'AR' | 'EN'; t: (key: string, options?: Record<string, unknown>) => string }) {
  return <section className="space-y-2">
    <h3 className="font-bold">{t('drawer.previousContactAttempts')}</h3>
    {row.contactAttempts.length ? row.contactAttempts.map((attempt) => <p key={attempt.id} className="text-xs text-slate-600">
      {t(`drawer.outcomes.${attempt.outcome}`, { defaultValue: attempt.outcome })} · {localizedName(attempt.user, lang)} · <bdi>{formatDateTime(attempt.createdAt, lang)}</bdi>{attempt.note ? ` · ${attempt.note}` : ''}
    </p>) : <p className="text-xs text-slate-500">{t('drawer.noAttempts')}</p>}
  </section>;
}

function localizedName(person: { fullNameAr?: string | null; fullNameEn?: string | null } | null | undefined, lang: 'AR' | 'EN'): string {
  return (lang === 'AR' ? person?.fullNameAr : person?.fullNameEn) || person?.fullNameAr || '';
}

function blockMessage(block: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  const code = typeof block === 'string'
    ? block
    : block && typeof block === 'object' && 'code' in block && typeof block.code === 'string'
      ? block.code
      : '';
  const keyByCode: Record<string, string> = {
    OUTSIDE_WORKING_HOURS: 'outsideWorkingHours',
    PROVIDER_ON_LEAVE: 'providerOnLeave',
    HOLIDAY: 'holiday',
    OUTSIDE_CLINIC_HOURS: 'outsideClinicHours',
    CLINIC_CLOSED_DAY: 'clinicClosedDay',
    DOUBLE_BOOKING: 'doubleBooking',
    BOOKING_IN_PAST: 'bookingInPast',
    providerOnLeave: 'providerOnLeave',
    scheduleUnavailable: 'scheduleUnavailable',
    providerUnavailable: 'providerOnLeave',
  };
  const messageKey = keyByCode[code];
  return messageKey ? t(`drawer.conflicts.${messageKey}`) : t('drawer.conflicts.default');
}

function localizeConflict(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  const code = error instanceof ApiError ? error.code : '';
  return t(`drawer.conflicts.${code || 'default'}`, { defaultValue: t('drawer.conflicts.default') });
}
